import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Database, Search, RefreshCw, Download, FileText, Eye,
  ArrowLeft, Loader2, CheckCircle2, AlertCircle, Play, X, Ghost, Archive,
  Phone, Mail, MessageSquare,
} from "lucide-react";
import { api, API } from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import AppLayout from "../components/AppLayout";
import DossierDiscussion from "../components/DossierDiscussion";
import RingoverCallHistory from "../components/RingoverCallHistory";
import ChellyImportCard from "../components/ChellyImportCard";
import { toast } from "sonner";

function bytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"]; let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

// Map of customfield_X -> friendly label (extracted from Chelly recon)
const CF_LABELS = {
  customfield_49: "Pièces d'identité",
  customfield_50: "Avis d'impôt",
  customfield_51: "Taxe foncière",
  customfield_52: "Facture électricité",
  customfield_69: "Justificatif domicile",
  customfield_70: "Bon de commande",
  customfield_71: "Fiche de contact",
  customfield_72: "Fiche de paie",
  customfield_73: "RIB",
  customfield_74: "Mandat mairie",
  customfield_75: "Mandat Enedis",
};
const cfLabel = (cf) => CF_LABELS[cf] || cf?.replace("customfield_", "Doc ") || "—";

export default function ChellyBackupPage() {
  const [state, setState] = useState(null);
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [skip, setSkip] = useState(0);
  const [limit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const [log, setLog] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [errorsList, setErrorsList] = useState([]);
  const [running, setRunning] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const refreshTimer = useRef(null);

  const loadState = async () => {
    try {
      const { data } = await api.get("/chelly_backup/state");
      setState(data);
    } catch (e) {
      // ignore
    }
  };

  const loadClients = async () => {
    setLoading(true);
    try {
      const params = { limit, skip };
      if (search) params.search = search;
      const { data } = await api.get("/chelly_backup/clients", { params });
      setClients(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Impossible de charger les clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadState(); loadClients(); }, []);
  useEffect(() => { loadClients(); /* eslint-disable-next-line */ }, [skip]);

  // auto-refresh state when a run is active
  useEffect(() => {
    const active = state?.live?.status === "scraping" || state?.live?.status === "starting" || state?.last_run?.status === "running";
    if (active) {
      refreshTimer.current = setInterval(() => { loadState(); }, 3500);
    } else if (refreshTimer.current) {
      clearInterval(refreshTimer.current); refreshTimer.current = null;
    }
    return () => { if (refreshTimer.current) clearInterval(refreshTimer.current); };
  }, [state?.live?.status, state?.last_run?.status]);

  // auto-refresh client list when scraper makes progress
  useEffect(() => {
    const active = state?.live?.status === "scraping" || state?.live?.status === "starting" || state?.last_run?.status === "running";
    if (!active) return;
    const t = setInterval(() => { loadClients(); }, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [state?.live?.clients_done, state?.last_run?.status]);

  const openClient = async (id) => {
    setSelected(id); setDetail(null);
    // Reflect in URL so notifications can deep-link
    setSearchParams({ client: String(id) }, { replace: true });
    try {
      const { data } = await api.get(`/chelly_backup/clients/${id}`);
      setDetail(data);
    } catch {
      toast.error("Impossible de charger le client");
    }
  };

  const closeClient = () => {
    setSelected(null); setDetail(null);
    // Clear the URL param
    if (searchParams.get("client")) {
      searchParams.delete("client");
      setSearchParams(searchParams, { replace: true });
    }
  };

  // Deep-link: open client drawer from ?client=X (from notifications, etc.)
  useEffect(() => {
    const cid = searchParams.get("client");
    if (cid && !selected) openClient(cid);
    // eslint-disable-next-line
  }, [searchParams]);

  const downloadFile = (clientId, d) => {
    const token = localStorage.getItem("norea_token");
    const url = `${API}/chelly_backup/files/${clientId}/${encodeURIComponent(d.customfield)}/${encodeURIComponent(d.filename)}`;
    // Use fetch+blob to attach Authorization header
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error("HTTP " + r.status);
        const blob = await r.blob();
        const a = document.createElement("a");
        const objUrl = URL.createObjectURL(blob);
        a.href = objUrl; a.download = d.filename;
        document.body.appendChild(a); a.click();
        setTimeout(() => { URL.revokeObjectURL(objUrl); a.remove(); }, 1500);
      })
      .catch(() => toast.error("Téléchargement impossible"));
  };

  const previewFile = (clientId, d) => {
    const token = localStorage.getItem("norea_token");
    const url = `${API}/chelly_backup/files/${clientId}/${encodeURIComponent(d.customfield)}/${encodeURIComponent(d.filename)}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error();
        const blob = await r.blob();
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
      })
      .catch(() => toast.error("Aperçu impossible"));
  };

  const startRun = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/chelly_backup/run");
      if (data.ok) {
        toast.success("Scraper lancé en arrière-plan");
        await loadState();
      } else {
        toast.error(data.error || "Impossible de lancer");
      }
    } catch (e) {
      toast.error("Erreur au lancement");
    } finally {
      setRunning(false);
    }
  };

  const loadLog = async () => {
    try {
      const { data } = await api.get("/chelly_backup/log", { params: { tail: 200 } });
      setLog(data.lines || []);
    } catch { /* */ }
  };

  useEffect(() => {
    if (!showLog) return;
    loadLog();
    const t = setInterval(loadLog, 2500);
    return () => clearInterval(t);
  }, [showLog]);

  const loadErrors = async () => {
    try {
      const { data } = await api.get("/chelly_backup/errors", { params: { limit: 500 } });
      setErrorsList(data.items || []);
    } catch { /* */ }
  };
  useEffect(() => { if (showErrors) loadErrors(); }, [showErrors]);

  const isRunning = state?.live?.status === "scraping" || state?.live?.status === "starting" || state?.last_run?.status === "running";
  const progress = useMemo(() => {
    const done = state?.live?.clients_done || 0;
    const total = state?.live?.total_clients || 0;
    return total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  }, [state]);

  return (
    <AppLayout>
      <PageHeader
        title="Backup Chelly CRM"
        subtitle="Sauvegarde complète de la base Chelly vers Norea (clients, statuts, documents PDF)"
        breadcrumb={[{ label: "Norea", to: "/" }, "Backup Chelly"]}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const token = localStorage.getItem("norea_token");
                // Trigger download via authenticated fetch → blob
                toast.message("Préparation du ZIP… cela peut prendre quelques minutes selon la taille");
                fetch(`${API}/chelly_backup/export.zip`, { headers: { Authorization: `Bearer ${token}` } })
                  .then(async (r) => {
                    if (!r.ok) throw new Error("HTTP " + r.status);
                    const blob = await r.blob();
                    const a = document.createElement("a");
                    const url = URL.createObjectURL(blob);
                    a.href = url; a.download = "chelly_backup_full.zip";
                    document.body.appendChild(a); a.click();
                    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 2000);
                    toast.success("Téléchargement démarré");
                  })
                  .catch((e) => toast.error("Échec téléchargement : " + e.message));
              }}
              className="px-3 py-2 text-sm rounded-md border border-norea-border bg-white hover:bg-norea-surfaceHover text-norea-text inline-flex items-center gap-2"
              title="Télécharger tous les PDFs sauvegardés en un seul ZIP — pour quitter définitivement Chelly"
              data-testid="chelly-export-zip-btn"
            >
              <Archive className="w-4 h-4" /> Tout exporter (ZIP)
            </button>
            <button
              onClick={() => setShowLog(true)}
              className="px-3 py-2 text-sm rounded-md border border-norea-border bg-white hover:bg-norea-surfaceHover text-norea-text inline-flex items-center gap-2"
              data-testid="chelly-show-log-btn"
            >
              <FileText className="w-4 h-4" /> Log
            </button>
            <button
              onClick={startRun}
              disabled={running || isRunning}
              className="px-3 py-2 text-sm rounded-md bg-norea-primary text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="chelly-run-btn"
            >
              {isRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {isRunning ? "Scraper en cours…" : "Lancer un backup"}
            </button>
          </div>
        }
      />

      <ChellyImportCard />

      {/* Progress / Stats card */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat
          label="Clients sauvegardés"
          value={state?.counts?.clients_in_db ?? "—"}
          sub={`sur ${state?.live?.total_clients || "—"} dans Chelly`}
          icon={<Database className="w-4 h-4 text-norea-primary" />}
        />
        <Stat
          label="Documents PDF"
          value={state?.counts?.docs_in_db ?? "—"}
          sub={state?.counts?.docs_failed ? `${state.counts.docs_failed} en erreur` : "Tous OK"}
          icon={<FileText className="w-4 h-4 text-emerald-600" />}
        />
        <Stat
          label="Espace disque"
          value={bytes(state?.counts?.disk_bytes || 0)}
          sub={
            state?.counts?.raw_bytes
              ? `${bytes(state.counts.raw_bytes)} brut → ${state.counts.compression_ratio}% (compression -${100 - state.counts.compression_ratio}%)`
              : "/app/data/chelly_backup"
          }
          icon={<Database className="w-4 h-4 text-sky-600" />}
        />
        <button
          onClick={() => setShowErrors(true)}
          className={`text-left bg-white rounded-lg border p-3 hover:shadow-sm transition ${state?.counts?.docs_missing_in_chelly ? "border-amber-300" : "border-norea-border"}`}
          data-testid="chelly-show-errors-btn"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-norea-textMuted">
            <Ghost className={`w-4 h-4 ${state?.counts?.docs_missing_in_chelly ? "text-amber-600" : "text-norea-textMuted"}`} />
            Fichiers fantômes
          </div>
          <div className="mt-1 text-2xl font-heading font-semibold text-norea-text">
            {state?.counts?.docs_missing_in_chelly ?? 0}
          </div>
          <div className="text-[11px] text-norea-textMuted">
            404 côté Chelly (entrée visible, PDF supprimé) — cliquer pour la liste
          </div>
        </button>
      </div>

      {isRunning && (
        <div className="mb-6 p-4 rounded-lg border border-norea-primary/30 bg-norea-primary/5">
          <div className="flex items-center gap-3 mb-2">
            <Loader2 className="w-4 h-4 text-norea-primary animate-spin" />
            <div className="text-sm font-semibold text-norea-text">
              Backup en cours · client #{state?.live?.last_client_id || "—"} · {state?.live?.clients_done || 0}/{state?.live?.total_clients || "?"}
            </div>
            <div className="ml-auto text-xs text-norea-textMuted">{progress}%</div>
          </div>
          <div className="h-2 bg-white rounded-full overflow-hidden border border-norea-border">
            <div
              className="h-full bg-gradient-to-r from-norea-primary to-norea-accent transition-all duration-700"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-2 text-xs text-norea-textMuted">
            {state?.live?.files_done || 0} fichiers téléchargés · {state?.live?.errors || 0} erreurs
          </div>
        </div>
      )}

      {/* Search bar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-norea-textMuted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setSkip(0); loadClients(); } }}
            placeholder="Rechercher nom, email, téléphone, ID…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-norea-border bg-white focus:outline-none focus:border-norea-primary"
            data-testid="chelly-search-input"
          />
        </div>
        <button
          onClick={() => { setSkip(0); loadClients(); loadState(); }}
          className="px-3 py-2 text-sm rounded-md border border-norea-border bg-white hover:bg-norea-surfaceHover inline-flex items-center gap-2"
          data-testid="chelly-refresh-btn"
        >
          <RefreshCw className="w-4 h-4" /> Actualiser
        </button>
        <div className="ml-auto text-xs text-norea-textMuted">
          {total} client{total > 1 ? "s" : ""} sauvegardé{total > 1 ? "s" : ""}
        </div>
      </div>

      {/* Clients table */}
      <div className="bg-white rounded-lg border border-norea-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-norea-bg border-b border-norea-border">
            <tr className="text-left text-xs uppercase tracking-wider text-norea-textMuted">
              <th className="px-4 py-2.5 font-medium">ID</th>
              <th className="px-4 py-2.5 font-medium">Nom</th>
              <th className="px-4 py-2.5 font-medium">Contact</th>
              <th className="px-4 py-2.5 font-medium">Ville</th>
              <th className="px-4 py-2.5 font-medium">Statut</th>
              <th className="px-4 py-2.5 font-medium text-center">Docs</th>
              <th className="px-4 py-2.5 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-norea-textMuted">
                <Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Chargement…
              </td></tr>
            ) : clients.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-norea-textMuted">
                {isRunning ? "Le scraper démarre… les premiers clients vont apparaître ici." : "Aucun client sauvegardé pour le moment."}
              </td></tr>
            ) : (
              clients.map((c) => (
                <tr key={c.client_id} className="border-b border-norea-border hover:bg-norea-surfaceHover cursor-pointer" onClick={() => openClient(c.client_id)} data-testid={`chelly-client-row-${c.client_id}`}>
                  <td className="px-4 py-2.5 font-mono text-xs text-norea-textMuted">{c.client_id}</td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-norea-text">
                      {c.form?.field_gender || ""} {c.form?.field_last_name || ""} {c.form?.field_first_name || ""}
                    </div>
                    <div className="text-xs text-norea-textMuted">{c.form?.field_address || ""}</div>
                  </td>
                  <td className="px-4 py-2.5 text-xs" onClick={(e) => e.stopPropagation()}>
                    {c.form?.field_email ? (
                      <a href={`mailto:${c.form.field_email}`} className="text-norea-primary hover:underline">{c.form.field_email}</a>
                    ) : "—"}
                    {c.form?.field_mobile && (
                      <div>
                        <a href={`tel:${c.form.field_mobile}`} className="text-norea-textMuted hover:text-norea-primary hover:underline">{c.form.field_mobile}</a>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs">
                    {c.form?.field_zip || ""} {c.form?.field_city || ""}
                  </td>
                  <td className="px-4 py-2.5 text-xs">
                    <span className="inline-flex px-2 py-0.5 rounded bg-norea-bg border border-norea-border">
                      {c.form?.field_status1 || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className="inline-flex items-center justify-center min-w-[24px] h-6 rounded-full bg-norea-primary/10 text-norea-primary text-xs font-semibold">
                      {(c.docs || []).length}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Eye className="inline w-4 h-4 text-norea-textMuted" />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-norea-border bg-norea-bg text-xs text-norea-textMuted">
            <div>
              {skip + 1} - {Math.min(skip + limit, total)} / {total}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSkip(Math.max(0, skip - limit))}
                disabled={skip === 0}
                className="px-3 py-1 rounded border border-norea-border bg-white hover:bg-norea-surfaceHover disabled:opacity-40"
              >Précédent</button>
              <button
                onClick={() => setSkip(skip + limit)}
                disabled={skip + limit >= total}
                className="px-3 py-1 rounded border border-norea-border bg-white hover:bg-norea-surfaceHover disabled:opacity-40"
              >Suivant</button>
            </div>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <DetailDrawer
          clientId={selected}
          data={detail}
          onClose={closeClient}
          onDownload={(d) => downloadFile(selected, d)}
          onPreview={(d) => previewFile(selected, d)}
        />
      )}

      {/* Log drawer */}
      {showLog && <LogDrawer log={log} onClose={() => setShowLog(false)} />}

      {/* Errors drawer */}
      {showErrors && (
        <ErrorsDrawer
          items={errorsList}
          onClose={() => setShowErrors(false)}
          onOpenClient={(id) => { setShowErrors(false); openClient(id); }}
        />
      )}
    </AppLayout>
  );
}

function Stat({ label, value, sub, icon }) {
  return (
    <div className="bg-white rounded-lg border border-norea-border p-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-norea-textMuted">
        {icon} {label}
      </div>
      <div className="mt-1 text-2xl font-heading font-semibold text-norea-text">{value}</div>
      <div className="text-[11px] text-norea-textMuted">{sub}</div>
    </div>
  );
}

function DetailDrawer({ clientId, data, onClose, onDownload, onPreview }) {
  // Extract all phone-like fields from the client form
  const phones = React.useMemo(() => {
    if (!data?.form) return [];
    const raw = [data.form.field_mobile, data.form.field_phone, data.form.field_telephone].filter(Boolean);
    return Array.from(new Set(raw));
  }, [data?.form]);
  const email = data?.form?.field_email;

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="chelly-client-detail">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-[760px] max-w-full bg-white border-l border-norea-border overflow-y-auto shadow-2xl">
        <div className="px-6 py-4 border-b border-norea-border flex items-center justify-between sticky top-0 bg-white z-10">
          <button onClick={onClose} className="text-norea-textMuted hover:text-norea-text inline-flex items-center gap-2 text-sm" data-testid="chelly-detail-close-btn">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <div className="text-xs font-mono text-norea-textMuted">Chelly #{clientId}</div>
        </div>
        {!data ? (
          <div className="p-8 text-center text-norea-textMuted">
            <Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Chargement…
          </div>
        ) : (
          <div className="px-6 py-5">
            <h2 className="font-heading text-2xl font-semibold text-norea-text">
              {data.form?.field_gender || ""} {data.form?.field_last_name || ""} {data.form?.field_first_name || ""}
            </h2>
            <div className="text-sm text-norea-textMuted mt-1">
              {data.form?.field_address || ""} · {data.form?.field_zip || ""} {data.form?.field_city || ""}
            </div>

            {/* Quick contact actions */}
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              {phones.map((p) => (
                <a
                  key={p}
                  href={`tel:${p}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-emerald-600 text-emerald-700 text-xs hover:bg-emerald-50 no-underline"
                  data-testid={`chelly-call-${p}`}
                >
                  <Phone className="w-3.5 h-3.5" /> {p}
                </a>
              ))}
              {email && (
                <a
                  href={`mailto:${email}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-norea-border text-norea-text text-xs hover:bg-norea-surfaceHover no-underline"
                  data-testid={`chelly-mailto-${email}`}
                >
                  <Mail className="w-3.5 h-3.5 text-norea-primary" /> {email}
                </a>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
              <Info label="Email" value={email} kind="email" />
              <Info label="Téléphone" value={data.form?.field_mobile} kind="phone" />
              <Info label="Statut" value={data.form?.field_status1} />
              <Info label="Statut RDV" value={data.form?.field_status2} />
              <Info label="Revenu / Montant" value={data.form?.field_revenue} />
              <Info label="Sauvegardé le" value={fmtDate(data.last_seen_at)} />
            </div>

            <h3 className="mt-6 mb-2 font-heading text-sm font-semibold text-norea-text uppercase tracking-wider">
              Documents ({(data.docs || []).length})
            </h3>
            <div className="space-y-1.5">
              {(data.docs || []).length === 0 && (
                <div className="text-sm text-norea-textMuted italic">Aucun document attaché.</div>
              )}
              {(data.docs || []).map((d, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-md border border-norea-border hover:bg-norea-surfaceHover" data-testid={`chelly-doc-${d.customfield}`}>
                  <FileText className="w-4 h-4 text-norea-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-norea-text truncate">{d.filename}</div>
                    <div className="text-[11px] text-norea-textMuted">
                      {cfLabel(d.customfield)} · {d.upload_date || "—"} · {d.size ? bytes(d.size) : ""}
                    </div>
                  </div>
                  {d.downloaded ? (
                    <>
                      <button
                        onClick={() => onPreview(d)}
                        className="p-1.5 rounded hover:bg-white"
                        title="Voir"
                        data-testid={`chelly-doc-preview-${d.customfield}`}
                      >
                        <Eye className="w-4 h-4 text-norea-textMuted" />
                      </button>
                      <button
                        onClick={() => onDownload(d)}
                        className="p-1.5 rounded hover:bg-white"
                        title="Télécharger"
                        data-testid={`chelly-doc-download-${d.customfield}`}
                      >
                        <Download className="w-4 h-4 text-norea-primary" />
                      </button>
                    </>
                  ) : d.error === "missing_in_chelly_404" ? (
                    <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded inline-flex items-center gap-1" title="Le fichier apparaît dans Chelly mais n'existe plus sur leur S3 (entrée fantôme)">
                      <Ghost className="w-3 h-3" /> fantôme Chelly
                    </span>
                  ) : (
                    <span className="text-xs text-amber-700 inline-flex items-center gap-1" title={d.error || "non téléchargé"}>
                      <AlertCircle className="w-3 h-3" /> non téléchargé
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Ringover call history */}
            <div className="mt-6">
              <RingoverCallHistory phones={phones} />
            </div>

            {/* Internal discussion + @mentions */}
            <div className="mt-6">
              <DossierDiscussion dossierId={String(clientId)} entityType="chelly_client" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value, kind }) {
  const display = (() => {
    if (!value) return "—";
    if (kind === "email") return (
      <a href={`mailto:${value}`} className="text-norea-primary hover:underline" data-testid={`info-mailto-${value}`}>{value}</a>
    );
    if (kind === "phone") return (
      <a href={`tel:${value}`} className="text-norea-primary hover:underline" data-testid={`info-tel-${value}`}>{value}</a>
    );
    return value;
  })();
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-norea-textMuted">{label}</div>
      <div className="text-sm text-norea-text">{display}</div>
    </div>
  );
}

function LogDrawer({ log, onClose }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [log]);
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-[900px] max-w-full bg-norea-text text-white overflow-hidden flex flex-col shadow-2xl">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between flex-shrink-0">
          <div className="font-mono text-xs uppercase tracking-wider opacity-80">Backup Log · live</div>
          <button onClick={onClose} className="opacity-70 hover:opacity-100" data-testid="chelly-log-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <pre ref={ref} className="flex-1 overflow-auto px-5 py-4 text-[11px] leading-relaxed font-mono whitespace-pre-wrap">
          {log.length === 0 ? "(no log yet)" : log.join("\n")}
        </pre>
      </div>
    </div>
  );
}

function ErrorsDrawer({ items, onClose, onOpenClient }) {
  const ghosts = items.filter((i) => i.error === "missing_in_chelly_404");
  const real = items.filter((i) => i.error !== "missing_in_chelly_404");

  const downloadGhostsCsv = () => {
    const token = localStorage.getItem("norea_token");
    fetch(`${API}/chelly_backup/ghosts.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error("HTTP " + r.status);
        const blob = await r.blob();
        const a = document.createElement("a");
        const url = URL.createObjectURL(blob);
        a.href = url; a.download = "chelly_fichiers_fantomes.csv";
        document.body.appendChild(a); a.click();
        setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1500);
        toast.success("CSV téléchargé");
      })
      .catch(() => toast.error("Téléchargement CSV impossible"));
  };

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="chelly-errors-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-[800px] max-w-full bg-white overflow-y-auto shadow-2xl border-l border-norea-border">
        <div className="px-6 py-4 border-b border-norea-border flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="font-heading text-xl font-semibold text-norea-text">Erreurs de téléchargement</div>
            <div className="text-xs text-norea-textMuted mt-0.5">
              {ghosts.length} fantôme{ghosts.length > 1 ? "s" : ""} · {real.length} erreur{real.length > 1 ? "s" : ""} réelle{real.length > 1 ? "s" : ""}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {ghosts.length > 0 && (
              <button
                onClick={downloadGhostsCsv}
                className="px-3 py-1.5 text-xs rounded-md border border-norea-border bg-white hover:bg-norea-surfaceHover inline-flex items-center gap-1.5"
                title="Exporter la liste des fantômes au format CSV (avec nom client + ville pour vérification manuelle dans Chelly)"
                data-testid="chelly-ghosts-csv-btn"
              >
                <Download className="w-3.5 h-3.5" /> CSV
              </button>
            )}
            <button onClick={onClose} className="text-norea-textMuted hover:text-norea-text p-1" data-testid="chelly-errors-close-btn">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-6">
          {ghosts.length > 0 && (
            <div>
              <div className="flex items-start gap-2 mb-3 p-3 rounded-md bg-amber-50 border border-amber-200">
                <Ghost className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-900 leading-relaxed">
                  <strong>Fichiers fantômes côté Chelly</strong> · L'entrée est encore listée dans la fiche client de Chelly, mais le fichier PDF a été supprimé de leur stockage S3 (HTTP 404). Ce n'est pas un bug du backup : on ne peut pas récupérer ce qui n'existe plus chez eux. Vous pouvez vérifier directement dans Chelly que la pièce manque réellement.
                </div>
              </div>
              <div className="space-y-1.5">
                {ghosts.map((e, i) => (
                  <ErrRow key={i} item={e} onOpenClient={onOpenClient} ghost />
                ))}
              </div>
            </div>
          )}

          {real.length > 0 && (
            <div>
              <div className="flex items-start gap-2 mb-3 p-3 rounded-md bg-red-50 border border-red-200">
                <AlertCircle className="w-4 h-4 text-red-700 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-red-900 leading-relaxed">
                  <strong>Erreurs techniques</strong> · Ces fichiers existent dans Chelly mais n'ont pas pu être téléchargés (timeout, erreur réseau…). Le scraper retentera automatiquement au prochain run.
                </div>
              </div>
              <div className="space-y-1.5">
                {real.map((e, i) => (
                  <ErrRow key={i} item={e} onOpenClient={onOpenClient} />
                ))}
              </div>
            </div>
          )}

          {items.length === 0 && (
            <div className="text-center py-8 text-norea-textMuted text-sm">
              <CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-emerald-600" />
              Aucune erreur. Tous les fichiers ont été téléchargés.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ErrRow({ item, onOpenClient, ghost }) {
  return (
    <button
      onClick={() => onOpenClient(item.client_id)}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-md border border-norea-border hover:bg-norea-surfaceHover text-left"
      data-testid={`chelly-err-${item.client_id}-${item.customfield}`}
    >
      {ghost ? <Ghost className="w-4 h-4 text-amber-600 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-sm text-norea-text truncate">{item.filename}</div>
        <div className="text-[11px] text-norea-textMuted">
          Client #{item.client_id} · {item.customfield} · {item.error}
        </div>
      </div>
      <ArrowLeft className="w-3.5 h-3.5 text-norea-textMuted rotate-180 flex-shrink-0" />
    </button>
  );
}
